import React, { useEffect, useState, useMemo } from "react";
import { Save, RotateCcw, Plus, Trash2, Building2, Fence, X, Layers } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

/**
 * Ground Editor v2 — multi-surface model.
 * Each side carries any number of `surfaces` (pitch-side fence, back wall, stand back,
 * stand roof, building wall) each with its own dimensions. Sponsors get bespoke banners
 * placed at exact metre positions on a chosen surface.
 *
 * The editor manages: pitch dimensions, per-side stand/dugouts/surfaces, and a tenant-wide
 * "Size library" of reusable banner sizes (e.g. Standard 8x2, Big back-wall 3x1.5).
 */

const SIDE_LABELS = { N: "North", E: "East", S: "South", W: "West" };
const SIDE_HINTS = {
    N: "Top of the pitch — typically the away end at grassroots grounds.",
    S: "Bottom of the pitch — usually where the main stand and dugouts sit.",
    W: "Left end of the pitch — behind the west goal.",
    E: "Right end of the pitch — behind the east goal.",
};
const SURFACE_TYPES = [
    { key: "pitch_side_fence", label: "Pitch-side fence",  hint: "The low boards right at the touchline." },
    { key: "back_wall",        label: "Back perimeter wall",hint: "The wall behind the pitch-side fence." },
    { key: "stand_back",       label: "Stand back wall",   hint: "Big banners on the rear of a stand." },
    { key: "stand_roof",       label: "Stand roof strip",  hint: "Top edge of a stand — high-visibility." },
    { key: "building_wall",    label: "Building / clubhouse wall", hint: "Brick or rendered wall, often near a clubhouse." },
];

const newId = () => `n_${Math.random().toString(36).slice(2, 11)}`;
const cloneGround = (g) => JSON.parse(JSON.stringify(g));

const newSurface = (type = "pitch_side_fence") => ({
    id: newId(), type, name: "", width_m: 24, height_m: type === "stand_back" ? 1.5 : type === "stand_roof" ? 0.5 : 0.6,
    banners: [],
});

const newSide = (sideKey) => ({ side: sideKey, stand: null, surfaces: [], dugouts: [], fence: null });

const makeDefault = () => ({
    pitch_length_m: 100,
    pitch_width_m: 64,
    size_presets: [
        { id: "preset_std", name: "Standard 8x2 board", width_m: 2.4, height_m: 0.6 },
        { id: "preset_half", name: "Half board", width_m: 1.2, height_m: 0.6 },
        { id: "preset_big", name: "Back-wall big", width_m: 4.0, height_m: 1.2 },
    ],
    sides: ["N", "E", "S", "W"].map(newSide),
});

export default function GroundEditor({ tenantId, queryString = "", initialGround, onClose, onSaved }) {
    const seed = initialGround ? cloneGround(initialGround) : makeDefault();
    if (!seed.sides || seed.sides.length === 0) seed.sides = ["N", "E", "S", "W"].map(newSide);
    if (!seed.size_presets) seed.size_presets = makeDefault().size_presets;
    // Ensure each side has a `surfaces` array (legacy v1 may have only `fence`)
    seed.sides.forEach((s) => { if (!s.surfaces) s.surfaces = []; });

    const [draft, setDraft] = useState(seed);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const setSide = (sideKey, patch) => {
        setDraft((d) => ({
            ...d,
            sides: d.sides.map((s) => (s.side === sideKey ? { ...s, ...patch } : s)),
        }));
    };

    const totalSlots = useMemo(() => {
        let total = 6;
        for (const side of draft.sides || []) {
            if (side.stand) total += Math.max(1, Math.min(6, side.stand.banners || 1));
            for (const surf of side.surfaces || []) total += (surf.banners || []).length;
            total += (side.dugouts || []).length;
        }
        return total;
    }, [draft]);

    const save = async () => {
        try { setBusy(true); setErr(""); await api.put(`/pitch/ground${queryString}`, { ground: draft }); onSaved?.(); }
        catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };
    const revertToDefault = async () => {
        if (!window.confirm("Revert this ground to the default 18-slot template? Custom side data will be lost.")) return;
        try { setBusy(true); await api.put(`/pitch/ground${queryString}`, { ground: null }); onSaved?.(); }
        catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };

    return (
        <div className="space-y-6" data-testid="ground-editor">
            {/* HEADER */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-3">Ground builder</div>
                    <h1 className="font-sport text-4xl md:text-5xl leading-none uppercase">Map every metre.</h1>
                    <p className="font-editorial italic text-zinc-400 mt-3 text-lg">
                        Add surfaces. Set their dimensions. Place sponsors as bespoke banners.
                    </p>
                </div>
                <div className="text-right">
                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Banners + slots</div>
                    <div className="font-display font-extrabold text-3xl tracking-tightest" style={{ color: "var(--tenant-primary)" }} data-testid="ground-total-slots">
                        {totalSlots}
                    </div>
                </div>
            </div>

            {err && <div className="border border-rose-500/40 text-rose-300 text-sm px-3 py-2.5" data-testid="ground-err">{err}</div>}

            {/* PITCH DIMS */}
            <div className="border hairline p-5">
                <div className="text-sm font-display font-bold tracking-tight mb-1">Pitch dimensions</div>
                <div className="text-[11px] text-zinc-500 mb-3">FA standard is 100m × 64m.</div>
                <div className="grid grid-cols-2 gap-4 max-w-md">
                    <NumberInput label="Length (m)" value={draft.pitch_length_m} onChange={(v) => setDraft({ ...draft, pitch_length_m: v })} testId="pitch-length" />
                    <NumberInput label="Width (m)" value={draft.pitch_width_m} onChange={(v) => setDraft({ ...draft, pitch_width_m: v })} testId="pitch-width" />
                </div>
            </div>

            {/* SIZE LIBRARY */}
            <SizeLibrary presets={draft.size_presets} onChange={(presets) => setDraft({ ...draft, size_presets: presets })} />

            {/* SIDES */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {draft.sides.map((s) => (
                    <SideCard key={s.side} side={s} onChange={(patch) => setSide(s.side, patch)} sizePresets={draft.size_presets} />
                ))}
            </div>

            {/* SAVE BAR */}
            <div className="border hairline p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="text-[11px] text-zinc-500">
                    Saving updates the live virtual pitch immediately. Existing sponsor assignments survive where banner IDs match.
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={revertToDefault} disabled={busy} className="text-xs uppercase tracking-ultra-wide border hairline px-3 py-2 text-zinc-300 hover:border-white/30 inline-flex items-center gap-1.5 transition-colors disabled:opacity-40" data-testid="ground-revert-btn">
                        <RotateCcw size={11} /> Use default template
                    </button>
                    <button onClick={onClose} disabled={busy} className="text-xs uppercase tracking-ultra-wide border hairline px-3 py-2 text-zinc-300 hover:border-white/30 transition-colors disabled:opacity-40" data-testid="ground-cancel-btn">Cancel</button>
                    <button onClick={save} disabled={busy} className="text-xs uppercase tracking-ultra-wide font-display font-bold px-4 py-2 inline-flex items-center gap-1.5 transition-opacity disabled:opacity-40" style={{ background: "var(--sb-amber)", color: "#000" }} data-testid="ground-save-btn">
                        <Save size={12} /> {busy ? "Saving…" : "Save ground"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---------- Size library ----------
const SizeLibrary = ({ presets, onChange }) => {
    const add = () => onChange([...(presets || []), { id: newId(), name: "New size", width_m: 2, height_m: 0.6 }]);
    const update = (i, patch) => onChange(presets.map((p, idx) => idx === i ? { ...p, ...patch } : p));
    const remove = (i) => onChange(presets.filter((_, idx) => idx !== i));
    return (
        <div className="border hairline p-5" data-testid="size-library">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="text-sm font-display font-bold tracking-tight inline-flex items-center gap-2">
                        <Layers size={14} className="text-[var(--sb-amber)]" /> Size library
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">Reusable banner sizes for quick assignment. Custom is always available too.</div>
                </div>
                <button type="button" onClick={add} className="text-xs uppercase tracking-ultra-wide border hairline px-2.5 py-1.5 text-zinc-300 hover:border-white/30 inline-flex items-center gap-1.5 transition-colors" data-testid="size-library-add">
                    <Plus size={11} /> New size
                </button>
            </div>
            {(!presets || presets.length === 0) ? (
                <div className="text-xs text-zinc-500 italic">No presets yet — add one to make assignment faster.</div>
            ) : (
                <div className="space-y-1.5">
                    {presets.map((p, i) => (
                        <div key={p.id || i} className="grid grid-cols-12 gap-2 items-center" data-testid={`size-preset-${i}`}>
                            <input value={p.name} onChange={(e) => update(i, { name: e.target.value })} placeholder="Name"
                                className="col-span-5 bg-transparent border hairline px-2.5 py-1.5 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-white/30" />
                            <NumberInput label="" value={p.width_m} step="0.1" onChange={(v) => update(i, { width_m: v })} extra="col-span-3" placeholder="W (m)" />
                            <NumberInput label="" value={p.height_m} step="0.1" onChange={(v) => update(i, { height_m: v })} extra="col-span-3" placeholder="H (m)" />
                            <button type="button" onClick={() => remove(i)} className="col-span-1 text-rose-400 hover:text-rose-300 p-1.5 inline-flex items-center justify-center"><Trash2 size={12} /></button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ---------- Side card ----------
const SideCard = ({ side, onChange, sizePresets }) => {
    const surfaces = side.surfaces || [];
    const addSurface = (type) => onChange({ surfaces: [...surfaces, newSurface(type)] });
    const updateSurface = (id, patch) => onChange({ surfaces: surfaces.map((s) => s.id === id ? { ...s, ...patch } : s) });
    const removeSurface = (id) => onChange({ surfaces: surfaces.filter((s) => s.id !== id) });

    return (
        <div className="border hairline p-5" data-testid={`side-card-${side.side}`}>
            <div className="flex items-baseline justify-between mb-1">
                <div>
                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Side</div>
                    <h3 className="font-sport text-2xl uppercase tracking-tightest leading-none mt-1">{SIDE_LABELS[side.side]}</h3>
                </div>
                <span className="text-[10px] text-zinc-600 italic">{side.side}</span>
            </div>
            <p className="text-[11px] text-zinc-500 mb-4">{SIDE_HINTS[side.side]}</p>

            {/* STAND */}
            <FeatureToggle icon={<Building2 size={13} />} label="Stand"
                enabled={!!side.stand}
                onToggle={(on) => onChange({ stand: on ? { name: "", banners: 1, sponsor_naming_mode: "fixed_name_plus_banner" } : null })}
                testId={`side-${side.side}-stand-toggle`} />
            {side.stand && <StandConfig stand={side.stand} onChange={(p) => onChange({ stand: { ...side.stand, ...p } })} side={side.side} />}

            {/* SURFACES */}
            <div className="mt-3">
                <div className="flex items-center justify-between mb-2">
                    <div className="inline-flex items-center gap-2 text-sm">
                        <Fence size={13} className="text-zinc-400" /> Surfaces
                        <span className="text-[10px] text-zinc-600">{surfaces.length}</span>
                    </div>
                    <SurfaceTypeAdder onAdd={addSurface} testId={`side-${side.side}-add-surface`} />
                </div>
                {surfaces.length === 0 ? (
                    <div className="text-xs text-zinc-500 italic px-2 py-2 border hairline">No surfaces on this side yet — add one above.</div>
                ) : (
                    <div className="space-y-3">
                        {surfaces.map((surf) => (
                            <SurfaceConfig key={surf.id} side={side.side} surface={surf}
                                onChange={(patch) => updateSurface(surf.id, patch)}
                                onRemove={() => removeSurface(surf.id)}
                                sizePresets={sizePresets || []} />
                        ))}
                    </div>
                )}
            </div>

            {/* DUGOUTS */}
            <div className="mt-3">
                <FeatureToggle icon={<span className="text-xs">▢</span>} label="Dugouts"
                    enabled={(side.dugouts || []).length > 0}
                    onToggle={(on) => onChange({ dugouts: on ? [{ name: "Home dugout" }] : [] })}
                    testId={`side-${side.side}-dugouts-toggle`} />
                {side.dugouts && side.dugouts.length > 0 && <DugoutsConfig dugouts={side.dugouts} onChange={(d) => onChange({ dugouts: d })} side={side.side} />}
            </div>
        </div>
    );
};

const SurfaceTypeAdder = ({ onAdd, testId }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="relative">
            <button type="button" onClick={() => setOpen(!open)}
                className="text-[10px] uppercase tracking-ultra-wide border hairline px-2.5 py-1 text-zinc-300 hover:border-white/30 inline-flex items-center gap-1.5 transition-colors"
                data-testid={testId}>
                <Plus size={10} /> Add surface
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
                    <div className="absolute right-0 top-full mt-1 z-20 w-[280px] border hairline bg-[#060b07]">
                        {SURFACE_TYPES.map((t) => (
                            <button key={t.key} type="button" onClick={() => { onAdd(t.key); setOpen(false); }}
                                className="w-full text-left px-3 py-2 hover:bg-white/[0.04] border-b hairline last:border-b-0 transition-colors"
                                data-testid={`add-surface-type-${t.key}`}>
                                <div className="text-sm">{t.label}</div>
                                <div className="text-[10px] text-zinc-500">{t.hint}</div>
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

const SurfaceConfig = ({ side, surface, onChange, onRemove, sizePresets }) => {
    const typeMeta = SURFACE_TYPES.find((t) => t.key === surface.type) || SURFACE_TYPES[0];
    const banners = surface.banners || [];
    const cursorEnd = banners.reduce((max, b) => Math.max(max, (b.position_m || 0) + (b.width_m || 0)), 0);

    const addBanner = () => {
        const preset = sizePresets[0] || { width_m: 2.4, height_m: 0.6 };
        const startPos = Math.min(cursorEnd, Math.max(0, surface.width_m - preset.width_m));
        onChange({
            banners: [...banners, {
                id: newId(), sponsor_id: null,
                width_m: preset.width_m, height_m: preset.height_m,
                position_m: startPos,
                size_preset_id: preset.id, notes: "", kind: "banner",
            }],
        });
    };

    const addGap = () => {
        const gapWidth = 0.5;  // sensible default — easy to edit
        const startPos = Math.min(cursorEnd, Math.max(0, surface.width_m - gapWidth));
        onChange({
            banners: [...banners, {
                id: newId(), sponsor_id: null,
                width_m: gapWidth, height_m: surface.height_m || 0.6,
                position_m: startPos,
                size_preset_id: null, notes: "", kind: "spacer",
            }],
        });
    };

    const updateBanner = (i, patch) => onChange({ banners: banners.map((b, idx) => idx === i ? { ...b, ...patch } : b) });
    const removeBanner = (i) => onChange({ banners: banners.filter((_, idx) => idx !== i) });
    const utilM = banners.reduce((acc, b) => acc + (b.width_m || 0), 0);
    const utilPct = surface.width_m > 0 ? Math.min(100, Math.round((utilM / surface.width_m) * 100)) : 0;

    return (
        <div className="border hairline p-3" data-testid={`surface-${surface.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-ultra-wide text-[var(--sb-amber)]">{typeMeta.label}</div>
                    <input value={surface.name || ""} onChange={(e) => onChange({ name: e.target.value })}
                        placeholder={typeMeta.label}
                        className="w-full bg-transparent text-sm text-white placeholder:text-zinc-600 outline-none border-b hairline focus:border-white/30 mt-1"
                        data-testid={`surface-${surface.id}-name`} />
                </div>
                <button type="button" onClick={onRemove} className="text-rose-400 hover:text-rose-300 p-1 shrink-0"><Trash2 size={12} /></button>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
                <NumberInput label="Width (m)" value={surface.width_m} step="0.1" onChange={(v) => onChange({ width_m: v })} testId={`surface-${surface.id}-width`} />
                <NumberInput label="Height (m)" value={surface.height_m} step="0.1" onChange={(v) => onChange({ height_m: v })} testId={`surface-${surface.id}-height`} />
            </div>
            {/* Util bar */}
            <div className="mb-3">
                <div className="flex items-center justify-between text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1">
                    <span>Filled · {utilM.toFixed(1)}m of {surface.width_m.toFixed(1)}m</span>
                    <span>{utilPct}%</span>
                </div>
                <div className="h-1.5 bg-white/[0.05]">
                    <div className="h-full" style={{ width: `${utilPct}%`, background: "var(--sb-amber)" }} />
                </div>
            </div>
            {/* Banners on this surface */}
            <div className="space-y-2">
                {banners.length === 0 ? (
                    <div className="text-[11px] text-zinc-500 italic">No banners placed yet.</div>
                ) : banners.map((b, i) => (
                    <BannerRow key={b.id} banner={b} idx={i} surface={surface} sizePresets={sizePresets}
                        onChange={(patch) => updateBanner(i, patch)} onRemove={() => removeBanner(i)} sideKey={side} />
                ))}
                <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={addBanner}
                        className="text-[10px] uppercase tracking-ultra-wide border hairline px-2 py-1.5 text-zinc-300 hover:border-white/30 inline-flex items-center gap-1.5 justify-center"
                        data-testid={`surface-${surface.id}-add-banner`}>
                        <Plus size={11} /> Add banner
                    </button>
                    <button type="button" onClick={addGap}
                        className="text-[10px] uppercase tracking-ultra-wide border hairline px-2 py-1.5 text-zinc-300 hover:border-white/30 inline-flex items-center gap-1.5 justify-center"
                        data-testid={`surface-${surface.id}-add-gap`}>
                        <Plus size={11} /> Add gap
                    </button>
                </div>
            </div>
        </div>
    );
};

const BannerRow = ({ banner, idx, surface, sizePresets, onChange, onRemove, sideKey }) => {
    const isSpacer = banner.kind === "spacer";
    const onPreset = (presetId) => {
        const p = sizePresets.find((sp) => sp.id === presetId);
        if (p) onChange({ size_preset_id: p.id, width_m: p.width_m, height_m: p.height_m });
        else onChange({ size_preset_id: null });
    };
    if (isSpacer) {
        return (
            <div className="grid grid-cols-12 gap-1.5 items-center bg-[rgba(255,255,255,0.02)] border-l-2 border-zinc-700 pl-2" data-testid={`spacer-${sideKey}-${surface.id}-${idx}`}>
                <div className="col-span-4 text-[10px] uppercase tracking-ultra-wide text-zinc-500 inline-flex items-center gap-1.5">
                    <span className="inline-block w-1 h-3 bg-zinc-600" /><span className="inline-block w-1 h-3 bg-zinc-600" /> Gap
                </div>
                <NumberInput label="" value={banner.width_m} step="0.1" onChange={(v) => onChange({ width_m: v })} extra="col-span-3" placeholder="Gap (m)" />
                <NumberInput label="" value={banner.position_m} step="0.1" onChange={(v) => onChange({ position_m: v })} extra="col-span-4" placeholder="@m" />
                <button type="button" onClick={onRemove} className="col-span-1 text-rose-400 hover:text-rose-300 p-1.5 inline-flex items-center justify-center"><Trash2 size={11} /></button>
            </div>
        );
    }
    return (
        <div className="grid grid-cols-12 gap-1.5 items-center" data-testid={`banner-${sideKey}-${surface.id}-${idx}`}>
            <select value={banner.size_preset_id || ""} onChange={(e) => onPreset(e.target.value)}
                className="col-span-4 bg-[#060b07] border hairline px-2 py-1.5 text-xs text-white outline-none focus:border-white/30">
                <option value="">Custom…</option>
                {sizePresets.map((p) => (
                    <option key={p.id} value={p.id}>{p.name} ({p.width_m}×{p.height_m})</option>
                ))}
            </select>
            <NumberInput label="" value={banner.width_m} step="0.1" onChange={(v) => onChange({ width_m: v, size_preset_id: null })} extra="col-span-2" placeholder="W" />
            <NumberInput label="" value={banner.height_m} step="0.1" onChange={(v) => onChange({ height_m: v, size_preset_id: null })} extra="col-span-2" placeholder="H" />
            <NumberInput label="" value={banner.position_m} step="0.1" onChange={(v) => onChange({ position_m: v })} extra="col-span-3" placeholder="@m" />
            <button type="button" onClick={onRemove} className="col-span-1 text-rose-400 hover:text-rose-300 p-1.5 inline-flex items-center justify-center"><Trash2 size={11} /></button>
        </div>
    );
};

// ---------- Common ----------
const FeatureToggle = ({ icon, label, enabled, onToggle, testId }) => (
    <button type="button" onClick={() => onToggle(!enabled)}
        className={`w-full flex items-center justify-between px-3 py-2 border transition-colors mb-2 ${enabled ? "border-[var(--sb-amber)] bg-[rgba(245,158,11,0.04)]" : "hairline hover:border-white/30"}`}
        data-testid={testId}>
        <span className="inline-flex items-center gap-2 text-sm"><span className="text-zinc-400">{icon}</span><span>{label}</span></span>
        <span className={`relative inline-flex h-4 w-7 items-center rounded-full ${enabled ? "bg-[var(--sb-amber)]" : "bg-white/10"}`}>
            <span className={`inline-block h-3 w-3 transform bg-white transition-transform ${enabled ? "translate-x-3.5" : "translate-x-0.5"}`} />
        </span>
    </button>
);

const StandConfig = ({ stand, onChange, side }) => (
    <div className="ml-3 pl-3 border-l-2 border-[rgba(245,158,11,0.4)] py-2 space-y-3 mb-2">
        <label className="block">
            <span className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Stand name</span>
            <input value={stand.name} onChange={(e) => onChange({ name: e.target.value })} placeholder="e.g. The Old Stand"
                className="mt-1 w-full bg-transparent border hairline px-2.5 py-1.5 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-white/30"
                data-testid={`side-${side}-stand-name`} />
        </label>
        <div className="grid grid-cols-2 gap-3">
            <NumberInput label="Banners" value={stand.banners} min={1} max={6} onChange={(v) => onChange({ banners: Math.max(1, Math.min(6, v || 1)) })} testId={`side-${side}-stand-banners`} />
            <label className="block">
                <span className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Naming mode</span>
                <select value={stand.sponsor_naming_mode} onChange={(e) => onChange({ sponsor_naming_mode: e.target.value })}
                    className="mt-1 w-full bg-[#060b07] border hairline px-2.5 py-1.5 text-sm text-white outline-none focus:border-white/30"
                    data-testid={`side-${side}-stand-mode`}>
                    <option value="fixed_name_plus_banner">Fixed name + banner</option>
                    <option value="stand_name_is_sponsor">Sponsor names the stand</option>
                </select>
            </label>
        </div>
    </div>
);

const DugoutsConfig = ({ dugouts, onChange, side }) => (
    <div className="ml-3 pl-3 border-l-2 border-[rgba(245,158,11,0.4)] py-2 space-y-2">
        {dugouts.map((d, i) => (
            <div key={i} className="flex items-center gap-2">
                <input value={d.name} onChange={(e) => onChange(dugouts.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))}
                    placeholder={i === 0 ? "Home dugout" : "Away dugout"}
                    className="flex-1 bg-transparent border hairline px-2.5 py-1.5 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-white/30"
                    data-testid={`side-${side}-dugout-name-${i}`} />
                <button type="button" onClick={() => onChange(dugouts.filter((_, idx) => idx !== i))} className="text-rose-400 hover:text-rose-300 p-1"><Trash2 size={12} /></button>
            </div>
        ))}
        {dugouts.length < 2 && (
            <button type="button" onClick={() => onChange([...dugouts, { name: dugouts.length === 0 ? "Home dugout" : "Away dugout" }])}
                className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1.5 px-2 py-1.5"
                data-testid={`side-${side}-dugout-add`}>
                <Plus size={11} /> Add dugout
            </button>
        )}
    </div>
);

const NumberInput = ({ label, value, onChange, min = 0, max, step = "1", testId, extra = "", placeholder }) => (
    <label className={`block ${extra}`}>
        {label && <span className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">{label}</span>}
        <input type="number" min={min} max={max} step={step} value={value ?? 0} placeholder={placeholder}
            onChange={(e) => onChange(e.target.value === "" ? 0 : parseFloat(e.target.value))}
            className={`${label ? "mt-1" : ""} w-full bg-transparent border hairline px-2.5 py-1.5 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-white/30`}
            data-testid={testId} />
    </label>
);
